import { PreloadingStrategy, Route, RouterModule } from '@angular/router';
import { AppConfigService } from './app/core/app-config/app-config.service';
import { Observable, switchMap, timer } from 'rxjs';
import { AuthenticationService } from '@dop/authentication';
import { APP_INITIALIZER, enableProdMode, importProvidersFrom, LOCALE_ID } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { environment } from '@environments/environment';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { rootRoutes } from './app/features/features.routes';
import { AuthorizationConfiguration } from './app/shared/security/authorization.configuration';
import { provideAnimations } from '@angular/platform-browser/animations';
import { DatePipe } from '@angular/common';
import { DateService } from './app/shared/services/date.service';
import { TranslationRootModule } from '@dop/translation';
import { LocalizationService } from './app/shared/services/localization.service';
import { DynamicLocaleIdProvider } from './app/shared/localizer/dynamic.localeid.provider';
import { ErrorHandlerInterceptor } from './app/core/Interceptor/error-handler.interceptor';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { UserInfoEffects } from './app/store/effects/userinfo.effect';
import { userInfoReducer } from './app/store/reducers/userinfo.reducer';
import { providePrimeNG } from 'primeng/config';
import AppThemePreset from './styles/app-primeng-theme';
import { InactivityService } from './app/service/inactivity.service';
import { NgIdleModule } from '@ng-idle/core';

import('./bootstrap').catch((err) => console.error(err));

const initializeApp = (appConfig: AppConfigService) => {
  return async (): Promise<void> => {
    await appConfig.setConfiguration();
  };
};

export function getBaseUrl(): string {
  return document.getElementsByTagName('base')[0].href;
}

export function dopAuthorizationConfiguration(authenticationService: AuthenticationService): AuthorizationConfiguration {
  return new AuthorizationConfiguration(() => authenticationService.logout());
}

class PreloadAllDeferredStrategy implements PreloadingStrategy {
  public preload(_route: Route, load: () => Observable<unknown>): Observable<unknown> {
    return timer(10000).pipe(switchMap(() => load()));
  }
}

fetch(environment.configurationPath)
  .then((response) => response.json())
  .then(() => {
    if (environment.production) {
      enableProdMode();
    }

    bootstrapApplication(AppComponent, {
      providers: [
        { provide: 'BASE_URL', useFactory: getBaseUrl, deps: [] },
        {
          provide: LOCALE_ID,
          useClass: DynamicLocaleIdProvider,
          deps: [LocalizationService]
        },
        {
          provide: AuthorizationConfiguration,
          useFactory: dopAuthorizationConfiguration,
          deps: [AuthenticationService, InactivityService]
        },
        importProvidersFrom(
          RouterModule.forRoot(rootRoutes, { preloadingStrategy: PreloadAllDeferredStrategy }),
          NgIdleModule.forRoot(),
          TranslationRootModule,
          EffectsModule.forRoot([UserInfoEffects]),
          StoreModule.forRoot({
            userInfo: userInfoReducer
          })
        ),
        PreloadAllDeferredStrategy,
        AppConfigService,
        {
          provide: APP_INITIALIZER,
          useFactory: initializeApp,
          deps: [AppConfigService],
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: ErrorHandlerInterceptor,
          multi: true
        },
        providePrimeNG({
          theme: {
            preset: AppThemePreset,
            options: {
              cssLayer: {
                name: 'primeng',
                order: 'primeng,app-styles'
              }
            }
          },
          ripple: true
        }),
        provideHttpClient(withInterceptorsFromDi()),
        AuthenticationService,
        provideAnimations(),
        DatePipe,
        LocalizationService,
        DateService
      ]
    }).catch((err: unknown) => console.log(err));
  });
